import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiPanel,
  EuiSpacer,
  EuiStat,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { OpsanioApplianceData } from "../..";
import { AnketaResourcesEnum } from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { FormSkeleton } from "src/components/skeleton";
import { OplBoard, isOplItemClosed } from "src/questionnaires";
import { useOplApi } from "src/opsanio/opl/api";
import { fmtLongDate } from "src/opsanio/opl/format";
import {
  DueFilter,
  DueFilterChips,
  dueCountsOf,
  matchesDue,
  OpenPointsTable,
  toOpenPointRows,
} from "src/opsanio/opl/open-points";

const i18n = "anketa.opsanio.appliance.flyout.opl";
const page = "anketa.opsanio.opl";

/**
 * OPL summary inside the appliance flyout.
 *
 * Not the working surface (the board needs three columns and ~1300px): the
 * glance - how much is open, how much is late, when the next round is - and
 * the list of every open point, most urgent first. A point opens on the board.
 */
export const OpsanioApplianceFlyoutOpl = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const navigate = useNavigate();

  const instanceName = appliance.common?.metadata?.name?.trim() ?? "";
  const api = useOplApi(instanceName);

  const [board, setBoard] = useState<OplBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!api.canCall) return;
    setLoading(true);
    setError(null);
    setForbidden(false);
    const result = await api.board();
    if (result.status === 403) {
      setForbidden(true);
    } else if (!result.ok || !result.data) {
      setError(result.error ?? "Die OPL konnte nicht geladen werden.");
    } else {
      setBoard(result.data);
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceName, api.canCall]);

  const t = useCallback(
    (k: string, d: string, values?: Record<string, unknown>) =>
      translate(`${page}.${k}`, { defaultMessage: d, values }),
    [translate],
  );
  const [due, setDue] = useState<DueFilter>("all");
  const openRows = useMemo(() => toOpenPointRows(board?.items ?? []), [board]);
  const dueCounts = useMemo(() => dueCountsOf(openRows), [openRows]);
  const visibleRows = useMemo(
    () => openRows.filter((r) => matchesDue(r, due)),
    [openRows, due],
  );

  const stats = useMemo(() => {
    const items = board?.items ?? [];
    const open = items.filter((i) => !isOplItemClosed(i.status));
    const now = Date.now();
    const current = board?.meetings.find((m) => m.id === board.currentMeetingId);
    return {
      open: open.length,
      overdue: open.filter(
        (i) => i.dueDate && new Date(i.dueDate).getTime() < now,
      ).length,
      rounds: board?.meetings.length ?? 0,
      current,
    };
  }, [board]);

  const wrap = (body: React.ReactNode) => (
    <EuiPanel paddingSize="m" hasShadow={false} color="subdued">
      <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false} wrap>
        <EuiFlexItem>
          <EuiTitle size="xs">
            <h3>{translate(`${i18n}.tab`, { defaultMessage: "OPL / Regeltermine" })}</h3>
          </EuiTitle>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButtonEmpty
            size="s"
            iconType="list"
            onClick={() => navigate(`/${AnketaResourcesEnum.opsanio}/opl`)}
          >
            {translate(`${page}.overview.linkAll`, {
              defaultMessage: "Open points of all appliances",
            })}
          </EuiButtonEmpty>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            size="s"
            fill
            iconType="popout"
            iconSide="right"
            disabled={instanceName === ""}
            onClick={() =>
              navigate(
                `/${AnketaResourcesEnum.opsanio}/opl/${encodeURIComponent(instanceName)}`,
              )
            }
          >
            {translate(`${page}.openFull`, { defaultMessage: "Vollansicht öffnen" })}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiHorizontalRule margin="m" />
      {body}
    </EuiPanel>
  );

  if (instanceName === "") {
    return wrap(
      <EuiCallOut
        color="warning"
        iconType="help"
        title={translate(`${i18n}.noInstance.title`, {
          defaultMessage: "Keine Appliance-Kennung",
        })}
      >
        <p>
          {translate(`${i18n}.noInstance.body`, {
            defaultMessage:
              "Diese Appliance hat keinen VPC-Namen (common.metadata.name), daher kann keine OPL zugeordnet werden.",
          })}
        </p>
      </EuiCallOut>,
    );
  }

  if (loading && !board) return wrap(<FormSkeleton />);

  if (forbidden) {
    return wrap(
      <EuiCallOut
        color="warning"
        iconType="lock"
        title={translate(`${i18n}.forbidden.title`, {
          defaultMessage: "Kein Zugriff auf die OPL",
        })}
      >
        <p>
          {translate(`${i18n}.forbidden.body`, {
            defaultMessage:
              "Ihrer Rolle fehlt die Berechtigung `opsanio/opl`. Ein Administrator kann sie unter Admin → Berechtigungen vergeben.",
          })}
        </p>
      </EuiCallOut>,
    );
  }

  if (error) {
    return wrap(
      <EuiCallOut
        color="danger"
        iconType="alert"
        title={translate(`${i18n}.error.title`, { defaultMessage: "OPL nicht verfügbar" })}
      >
        <p>{error}</p>
        <EuiButton size="s" color="danger" iconType="refresh" onClick={load}>
          {translate(`${page}.error.retry`, { defaultMessage: "Erneut versuchen" })}
        </EuiButton>
      </EuiCallOut>,
    );
  }

  if (stats.rounds === 0) {
    return wrap(
      <EuiText size="s" color="subdued">
        <p>
          {translate(`${page}.empty.body`, {
            defaultMessage: "Die OPL beginnt mit der ersten Runde.",
          })}
        </p>
      </EuiText>,
    );
  }

  return wrap(
    <>
      <EuiFlexGroup gutterSize="l" wrap>
        <EuiFlexItem>
          <EuiStat
            title={`${stats.open}`}
            description={translate(`${page}.status.open`, { defaultMessage: "offen" })}
            titleSize="m"
          />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiStat
            title={`${stats.overdue}`}
            description={translate(`${page}.overdue`, { defaultMessage: "überfällig" })}
            titleSize="m"
            titleColor={stats.overdue > 0 ? "danger" : "default"}
          />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiStat
            title={stats.current?.key ?? "-"}
            description={translate(`${page}.round`, { defaultMessage: "Runde" })}
            titleSize="xs"
          />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiStat
            title={stats.current ? fmtLongDate(stats.current.date) : "-"}
            description={translate(`${page}.head.date`, { defaultMessage: "Datum" })}
            titleSize="xs"
          />
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="l" />

      {/* All open points of this appliance, most urgent first. */}
      <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false} wrap>
        <EuiFlexItem>
          <EuiTitle size="xxs">
            <h4>
              {translate(`${page}.overview.appliance.title`, {
                values: { count: openRows.length },
                defaultMessage: "Open points ({{count}})",
              })}
            </h4>
          </EuiTitle>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <DueFilterChips t={t} value={due} counts={dueCounts} onChange={setDue} />
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="s" />
      <OpenPointsTable
        t={t}
        rows={visibleRows}
        showAppliance={false}
        pageSize={10}
        onOpen={(row) =>
          navigate(
            `/${AnketaResourcesEnum.opsanio}/opl/${encodeURIComponent(instanceName)}?item=${encodeURIComponent(row.id)}`,
          )
        }
        emptyMessage={
          openRows.length === 0
            ? t("overview.empty", "No open points - everything is done.")
            : t("overview.noMatch", "No open points match the filters.")
        }
      />
    </>,
  );
};

export default OpsanioApplianceFlyoutOpl;
